/**
 * Auth layer for the admin console.
 *
 * We talk to Netlify Identity (GoTrue) directly via `gotrue-js` instead of the
 * `netlify-identity-widget`. The widget rendered its whole login UI inside an
 * iframe, which (a) shipped Netlify's own branding and (b) hid the email and
 * password fields from password managers (Dashlane, 1Password…) so autofill
 * never worked. Driving GoTrue ourselves lets the React app own a native,
 * autofillable form while keeping the exact same Identity backend, users and
 * JWTs.
 */
import GoTrue, { type User } from 'gotrue-js';
import type { AdminUser } from '../types';

/**
 * The GoTrue endpoint. In production it is same-origin
 * (admin.financeforquantum.com/.netlify/identity). On `npm run dev` we point
 * explicitly at the production instance — same behaviour as before.
 */
function apiUrl(): string {
  const isLocal = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
  return isLocal
    ? 'https://admin.financeforquantum.com/.netlify/identity'
    : `${location.origin}/.netlify/identity`;
}

const auth = new GoTrue({ APIUrl: apiUrl(), setCookie: false });

/* ---------------- change notifications ---------------- */
/* GoTrue has no event bus, so we keep a tiny one. It lets `logout()` (or an
   invite/recovery sign-in) fired from anywhere re-sync the React tree. */
type Listener = () => void;
const listeners = new Set<Listener>();
function emit() {
  listeners.forEach((l) => l());
}

/** Subscribe to auth changes. Returns an unsubscribe function. */
export function onAuthChange(cb: Listener): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/* ---------------- session reads ---------------- */

export function currentUser(): User | null {
  return auth.currentUser();
}

/** Normalise a GoTrue user into the app's lightweight shape. */
export function toAdminUser(u: User | null): AdminUser | null {
  if (!u) return null;
  const fullName = u.user_metadata?.full_name;
  return {
    id: u.id,
    email: u.email,
    full_name: typeof fullName === 'string' ? fullName : undefined,
  };
}

/**
 * Returns a fresh bearer token, refreshing it if it is close to expiry.
 * `null` when there is no session.
 */
export async function getJwt(): Promise<string | null> {
  const user = auth.currentUser();
  if (!user) return null;
  try {
    return await user.jwt();
  } catch {
    return null;
  }
}

/* ---------------- session writes ---------------- */

/** Email + password sign-in. `remember` persists the session to localStorage. */
export async function loginWithPassword(email: string, password: string): Promise<User> {
  const user = await auth.login(email.trim(), password, true);
  emit();
  return user;
}

/** Triggers the "reset your password" email. Resolves even if the email is unknown. */
export async function requestPasswordRecovery(email: string): Promise<void> {
  await auth.requestPasswordRecovery(email.trim());
}

/** Accept a Netlify Identity invite: set the first password and sign in. */
export async function acceptInvite(token: string, password: string): Promise<User> {
  const user = await auth.acceptInvite(token, password, true);
  emit();
  return user;
}

/** Confirm a sign-up via the emailed confirmation token. */
export async function confirmSignup(token: string): Promise<User> {
  const user = await auth.confirm(token, true);
  emit();
  return user;
}

/** Finish a password reset: redeem the recovery token, then set the new password. */
export async function setPasswordFromRecovery(token: string, password: string): Promise<User> {
  const recovered = await auth.recover(token, true);
  const updated = await recovered.update({ password });
  emit();
  return updated;
}

export async function logout(): Promise<void> {
  const u = auth.currentUser();
  try {
    if (u) await u.logout();
  } catch {
    /* Even if the revoke call fails (offline, expired token…) GoTrue clears the
       local session, so we still treat the user as signed out. */
  }
  emit();
}

/* ---------------- email-link tokens ---------------- */

export type HashToken =
  | { type: 'invite' | 'recovery' | 'confirm' | 'email_change'; token: string }
  | { type: 'error'; message: string };

/**
 * Netlify Identity links land back on the site with the action token in the URL
 * fragment (e.g. `#invite_token=…`, `#recovery_token=…`, or
 * `#error=access_denied&error_description=…`). The widget used to swallow these;
 * now we parse them ourselves and strip the fragment so it can't be replayed or
 * leak into logs.
 */
export function consumeHashToken(): HashToken | null {
  const hash = location.hash.replace(/^#\/?/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);

  const result = ((): HashToken | null => {
    if (params.has('error') || params.has('error_description')) {
      const desc = params.get('error_description') ?? params.get('error') ?? 'This link is invalid or has expired.';
      return { type: 'error', message: decodeURIComponent(desc.replace(/\+/g, ' ')) };
    }
    const invite = params.get('invite_token');
    if (invite) return { type: 'invite', token: invite };
    const recovery = params.get('recovery_token');
    if (recovery) return { type: 'recovery', token: recovery };
    const confirmation = params.get('confirmation_token');
    if (confirmation) return { type: 'confirm', token: confirmation };
    const emailChange = params.get('email_change_token');
    if (emailChange) return { type: 'email_change', token: emailChange };
    return null;
  })();

  if (result) {
    // Drop the fragment without adding a history entry.
    history.replaceState(null, '', location.pathname + location.search);
  }
  return result;
}

/** Best-effort, human-readable message from a GoTrue/HTTP error. */
export function authErrorMessage(err: unknown): string {
  const e = err as { json?: { error_description?: string; msg?: string; error?: string }; status?: number; message?: string };
  const fromJson = e?.json?.error_description || e?.json?.msg || e?.json?.error;
  if (e?.status === 400 || /invalid[_ ]grant|invalid login/i.test(fromJson ?? '')) {
    return 'Incorrect email or password.';
  }
  if (e?.status === 422) return 'Please check the details and try again.';
  if (fromJson) return fromJson;
  if (e?.message && !/failed to fetch/i.test(e.message)) return e.message;
  return 'Something went wrong. Please try again.';
}
